import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/components/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Gavel, Loader2, ShieldCheck, Star, Briefcase, X } from "lucide-react";
import { toast } from "sonner";

export function CleanerBidForm({ job, onChanged }) {
  const [bids, setBids] = useState([]);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const mine = bids.find((b) => b.status === "active");

  const load = useCallback(() => api.get(`/jobs/${job.id}/bids`).then((r) => {
    setBids(r.data);
    const a = r.data.find((b) => b.status === "active");
    if (a) { setAmount(String(a.amount)); setMessage(a.message || ""); }
  }), [job.id]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/bids`, { amount: Number(amount), message });
      toast.success(mine ? "Offer updated" : "Offer submitted");
      await load(); onChanged?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const withdraw = async () => {
    setBusy(true);
    try {
      await api.delete(`/jobs/${job.id}/bids/me`);
      toast.success("Offer withdrawn"); setAmount(""); setMessage("");
      await load(); onChanged?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (job.status !== "open") {
    const final = bids[0];
    return final ? <p className="text-sm text-muted-foreground" data-testid="bid-final-status">Your offer of {money(final.amount)} was {final.status}.</p> : null;
  }
  return (
    <div className="space-y-3" data-testid="cleaner-bid-form">
      <div className="flex items-center gap-2 text-sapphire"><Gavel className="w-4 h-4 text-ocean" /><span className="font-semibold text-sm">{mine ? "Your offer" : "Make an offer"}</span></div>
      {job.budget_amount > 0 && <p className="text-xs text-muted-foreground">Host budget: {money(job.budget_amount)}</p>}
      <div>
        <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Your price $</Label>
        <Input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="bid-amount" />
      </div>
      <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Short note to the host (optional)" data-testid="bid-message" />
      <Button className="w-full bg-ocean hover:bg-ocean/90" disabled={busy || !amount} onClick={submit} data-testid="submit-bid">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mine ? "Update Offer" : "Submit Offer"}
      </Button>
      {mine && <Button variant="outline" className="w-full" disabled={busy} onClick={withdraw} data-testid="withdraw-bid"><X className="w-4 h-4 mr-1" /> Withdraw Offer</Button>}
    </div>
  );
}

export function PMBidList({ job, onChanged }) {
  const [bids, setBids] = useState([]);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get(`/jobs/${job.id}/bids`).then((r) => setBids(r.data)), [job.id]);
  useEffect(() => { load(); }, [load]);

  const accept = async (bid) => {
    setBusy(bid.id);
    try {
      await api.post(`/jobs/${job.id}/bids/${bid.id}/accept`);
      toast.success(`${bid.cleaner.name} hired for ${money(bid.amount)}`);
      onChanged?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="border border-border rounded-md bg-white p-4" data-testid="pm-bid-list">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-sapphire flex items-center gap-2"><Gavel className="w-4 h-4 text-ocean" /> Offers ({bids.length})</h3>
        {job.budget_amount > 0 && <span className="text-xs text-muted-foreground">Budget {money(job.budget_amount)}</span>}
      </div>
      {bids.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-bids">No offers yet — eligible cleaners will be notified as they browse the marketplace.</p>}
      <div className="space-y-3">
        {bids.map((b) => (
          <div key={b.id} className={`rounded-md border p-3 ${b.status === "accepted" ? "border-emerald-400 bg-emerald-50" : "border-border"}`} data-testid={`bid-${b.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <button onClick={() => navigate(`/profile/${b.cleaner.id}`)} className="font-semibold text-ocean hover:underline" data-testid={`bid-cleaner-${b.id}`}>{b.cleaner.name}</button>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
                  {b.cleaner.background_check_status === "approved" && <span className="flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3 h-3" /> Verified</span>}
                  <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{b.cleaner.rating ?? "New"} ({b.cleaner.review_count})</span>
                  <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{b.cleaner.jobs_completed} jobs</span>
                </div>
                {b.message && <p className="text-sm text-slate-600 mt-2">“{b.message}”</p>}
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading font-black text-lg text-sapphire" data-testid={`bid-amount-${b.id}`}>{money(b.amount)}</div>
                {b.status === "active" && job.status === "open" && (
                  <Button size="sm" className="mt-2 bg-ocean hover:bg-ocean/90" disabled={busy === b.id} onClick={() => accept(b)} data-testid={`accept-bid-${b.id}`}>
                    {busy === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Hire"}
                  </Button>
                )}
                {b.status === "accepted" && <span className="text-xs font-semibold text-emerald-700">Hired</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
